import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { rfcsAPI } from '../../services/api';

const API_URL = '';

const Rfcs = () => {
  const [rfcs, setRfcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRfc, setEditingRfc] = useState(null);
  const [formData, setFormData] = useState({
    rfc_code: '',
    business_name: '',
    fiscal_regime: ''
  });
  const [constanciaFile, setConstanciaFile] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchRfcs();
  }, []);

  const fetchRfcs = async () => {
    try {
      setLoading(true);
      const response = await rfcsAPI.getAll();
      setRfcs(response.data.rfcs);
    } catch (err) {
      setError('Error al cargar los RFCs');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (rfc = null) => {
    if (rfc) {
      setEditingRfc(rfc);
      setFormData({
        rfc_code: rfc.rfc_code,
        business_name: rfc.business_name,
        fiscal_regime: rfc.fiscal_regime || ''
      });
      setPreviewImage(rfc.constancia_image ? `${API_URL}${rfc.constancia_image}` : null);
    } else {
      setEditingRfc(null);
      setFormData({
        rfc_code: '',
        business_name: '',
        fiscal_regime: ''
      });
      setPreviewImage(null);
    }
    setConstanciaFile(null);
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRfc(null);
    setFormError('');
    setConstanciaFile(null);
    setPreviewImage(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setConstanciaFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      const data = new FormData();
      data.append('rfc_code', formData.rfc_code);
      data.append('business_name', formData.business_name);
      data.append('fiscal_regime', formData.fiscal_regime);
      if (constanciaFile) {
        data.append('constancia', constanciaFile);
      }

      if (editingRfc) {
        await rfcsAPI.update(editingRfc.id, data);
      } else {
        await rfcsAPI.create(data);
      }
      closeModal();
      fetchRfcs();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rfc, force = false) => {
    const confirmMsg = force
      ? `¿ELIMINAR FORZADO el RFC "${rfc.rfc_code}"?\n\nEsto eliminará la asociación con las webs que lo usan. Esta acción no se puede deshacer.`
      : `¿Eliminar RFC ${rfc.rfc_code}?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await rfcsAPI.delete(rfc.id, force);
      fetchRfcs();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error al eliminar';
      if (errorMsg.includes('asociado') && !force) {
        if (window.confirm(`${errorMsg}\n\n¿Desea eliminar forzadamente y quitar la asociación de las webs?`)) {
          handleDelete(rfc, true);
        }
      } else {
        alert(errorMsg);
      }
    }
  };

  return (
    <AdminLayout>
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">RFC y Constancias Fiscales</h1>
            <p className="text-gray-600">Gestiona los datos fiscales para las cotizaciones</p>
          </div>
          <button onClick={() => openModal()} className="btn btn-primary">
            + Agregar RFC
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Cargando...</p>
            </div>
          ) : rfcs.length === 0 ? (
            <div className="col-span-full p-8 text-center text-gray-500">
              No hay RFCs registrados
            </div>
          ) : (
            rfcs.map((rfc) => (
              <div key={rfc.id} className="bg-white rounded-lg shadow overflow-hidden">
                {/* Constancia Image */}
                <div className="h-40 bg-gray-100 flex items-center justify-center">
                  {rfc.constancia_image ? (
                    <img
                      src={`${API_URL}${rfc.constancia_image}`}
                      alt="Constancia Fiscal"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <div className="text-gray-400 text-center">
                      <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm mt-1">Sin imagen</p>
                    </div>
                  )}
                </div>

                {/* Card Content */}
                <div className="p-4">
                  <h3 className="font-bold text-lg text-gray-900">{rfc.rfc_code}</h3>
                  <p className="text-gray-600 text-sm mt-1">{rfc.business_name}</p>
                  {rfc.fiscal_regime && (
                    <p className="text-gray-500 text-xs mt-1">{rfc.fiscal_regime}</p>
                  )}

                  <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
                    <button
                      onClick={() => openModal(rfc)}
                      className="text-primary-600 hover:text-primary-800 text-sm"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(rfc)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingRfc ? 'Editar RFC' : 'Nuevo RFC'}
              </h2>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label">RFC (12-13 caracteres)</label>
                  <input
                    type="text"
                    value={formData.rfc_code}
                    onChange={(e) => setFormData({ ...formData, rfc_code: e.target.value.toUpperCase().slice(0, 13) })}
                    className="input font-mono uppercase"
                    placeholder="Ej: NATJ880409DP6"
                    maxLength={13}
                    required
                  />
                </div>

                <div>
                  <label className="label">Razón Social / Nombre</label>
                  <input
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    className="input"
                    placeholder="Nombre o razón social"
                    required
                  />
                </div>

                <div>
                  <label className="label">Régimen Fiscal (opcional)</label>
                  <input
                    type="text"
                    value={formData.fiscal_regime}
                    onChange={(e) => setFormData({ ...formData, fiscal_regime: e.target.value })}
                    className="input"
                    placeholder="Ej: Persona Física con Actividad Empresarial"
                  />
                </div>

                <div>
                  <label className="label">Constancia de Situación Fiscal</label>
                  <div className="mt-1">
                    {previewImage && (
                      <div className="mb-3 border rounded-lg p-2 bg-gray-50">
                        <img
                          src={previewImage}
                          alt="Preview"
                          className="max-h-48 mx-auto object-contain"
                        />
                      </div>
                    )}
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept="image/*"
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG o GIF. Máximo 5MB.</p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" disabled={saving} className="btn btn-primary">
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Rfcs;
